import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom'; // Added useSearchParams
import { onAuthStateChanged } from 'firebase/auth';
import { auth, analytics } from '../firebase';
import {
  Check,
  Zap,
  ShieldCheck,
  HelpCircle,
  Loader2,
  Globe,
  ArrowLeft,
  Lock,
  CheckCircle2 // Added icon
} from 'lucide-react';

// --- CONFIGURATION ---
const PLANS = [
  {
    id: 'monthly-pro-basic',
 paystackPlanId: "PLN_vnym268int2smbv", // "3336068",
    name: 'Basic Pass',
    priceUSD: 20,
    priceNGN: 27050,
    credits: { basic: 3_000_000, diamond: 200_000 },
    features: [
      '3M light credits (Worker)',
         '200k diamond credits (Project Manager)',
     'Unlimited projects',    
	
  'Unused credits rollover to next month',
      'Standard Generation Speed',
      'Community Support'
    ],
    highlight: false
  },
  {
    id: 'monthly-pro-lightning',
 paystackPlanId: "PLN_etoaeqwwpy8g9dv", // "3336072",
    name: 'Lightening pass',
    priceUSD: 50,
    priceNGN: 67580,
    credits: { basic: 8_000_000, diamond: 400_000 },
    features: [
       '8M light credits (Worker)',                               '400k diamond credits (Project Manager)',                                'Unlimited projects',                                  'Community Support',    
  'Unused credits rollover to next month',                       'Team Collaboration (coming soon)',                        'Dedicated Support'
    ],
    highlight: true
  }
];

export default function Paywall() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams(); // To grab URL params
  
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  
  // Payment States
  const [processingId, setProcessingId] = useState(null);
  const [verifying, setVerifying] = useState(false); // New state for verification
  const [paymentSuccess, setPaymentSuccess] = useState(false); // New state for success modal
  const [error, setError] = useState('');

  // 1. Auth Guard
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
      } else {
        // If we are verifying a payment, don't redirect yet, wait for auth to resolve
        if (!window.location.search.includes('trxref')) {
            navigate('/auth', { state: { from: '/pricing' } });
        }
      }
      setAuthLoading(false);
    });
    return () => unsubscribe();
  }, [navigate]);

  // 2. VERIFICATION LOGIC (The "Claim" Step)
  useEffect(() => {
    const checkTransaction = async () => {
        // Only run if user is loaded and we have a reference in URL
        const reference = searchParams.get('trxref') || searchParams.get('reference');
        
        if (reference && user && !verifying && !paymentSuccess) {
            setVerifying(true);
            
            try {
                const BACKEND_URL = process.env.REACT_APP_CORE_SERVER_URL;
                console.log("Verifying transaction:", reference);

                const res = await fetch(`${BACKEND_URL}/claim-transaction`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        userId: user.uid,
                        reference: reference
                    })
                });

                const data = await res.json();

                if (data.ok) {
                    setPaymentSuccess(true);
                    // Clear URL params to clean up history
                    window.history.replaceState({}, document.title, window.location.pathname);
                    
                    // Redirect to console after 3 seconds
                    setTimeout(() => {
                        navigate('/console');
                    }, 3000);
                } else {
                    // Even if it fails (e.g. already claimed), if message says processed, treat as success
                    if(data.message && data.message.includes('already processed')) {
                         setPaymentSuccess(true);
                         setTimeout(() => navigate('/console'), 2000);
                    } else {
                         throw new Error(data.message || "Verification failed");
                    }
                }
            } catch (err) {
                console.error("Verification Error:", err);
                setError(err.message || "Failed to verify payment. Please contact support.");
            } finally {
                setVerifying(false);
            }
        }
    };

    if (!authLoading && user) {
        checkTransaction();
    }
  }, [user, authLoading, searchParams, navigate, verifying, paymentSuccess]);


  // 3. Initiate Payment
  const handleSubscribe = async (plan) => {
    if (!user) return navigate('/auth');

    setProcessingId(plan.id);
    setError('');

    try {
      const BACKEND_URL = process.env.REACT_APP_CORE_SERVER_URL;

      const resp = await fetch(`${BACKEND_URL}/create-payment-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.uid,
          email: user.email,
          name: user?.displayName || "Hollowpad User",
          planId: plan.paystackPlanId,
          amount: plan.priceNGN * 100,
          // CRITICAL: Redirect back to THIS page so useEffect above can run
          redirect_url: window.location.href 
        })
      });

	   // alert(resp);

      const response = await resp.json();

	  //  alert(response);
      console.log(response);

      if (!response.ok) throw new Error(response.error || "Failed to initiate transaction");

      // Redirect to Paystack
      if (response.page && response.page.url) {
        window.location.href = response.page.url;
      } else {
         // Fallback logic
         if(response.page && response.page.slug) {
            window.location.href = `https://paystack.com/pay/${response.page.slug}`;
         } else {
            throw new Error("Invalid payment link received.");
         }
      }

    } catch (err) {
      console.error("Payment Error:", err);
      setError(err.message || "Could not initialize payment.");
    } finally {
      setProcessingId(null);
    }
  };

  // --- LOADING / VERIFYING STATE ---
  if (authLoading || verifying) {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center space-y-4">
        <Loader2 className="animate-spin text-blue-500 w-12 h-12" />
        <p className="text-slate-400 text-sm animate-pulse">
            {verifying ? "Verifying your payment..." : "Loading account..."}
        </p>
      </div>
    );
  }

  // --- SUCCESS STATE OVERLAY ---
  if (paymentSuccess) {
      return (
        <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center animate-in zoom-in duration-500">
            <div className="bg-green-500/10 p-6 rounded-full mb-6 border border-green-500/20">
                <CheckCircle2 className="w-16 h-16 text-green-500" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
            <p className="text-slate-400 mb-8">Your account has been upgraded.</p>
            <div className="flex items-center gap-2 text-sm text-slate-500">
                <Loader2 className="w-4 h-4 animate-spin" /> Redirecting to console...
            </div>
        </div>
      );
  }

  // --- MAIN RENDER ---
  return (
    <div className="min-h-screen bg-slate-950 text-slate-200 font-sans selection:bg-blue-500 selection:text-white">

      {/* --- NAVBAR --- */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-950/90 backdrop-blur-xl border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
             <div className="flex items-center gap-2">
                <img src="/logo512.png" alt="Logo" className="w-8 h-8 rounded-lg" />
                <span className="font-bold text-lg text-white tracking-tight hidden md:block">Hollowpad</span>
             </div>
             <div className="h-6 w-px bg-slate-800 mx-2 hidden md:block"></div>
             <span className="bg-gradient-to-r from-blue-500 to-indigo-500 bg-clip-text text-transparent font-bold text-sm uppercase tracking-wider">
                Premium
             </span>
          </div>

          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm font-medium text-slate-400 hover:text-white transition-all bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-full border border-slate-800"
          >
            <ArrowLeft size={16} /> <span className="hidden sm:inline">Back to Home</span>
          </button>
        </div>
      </nav>

      <div className="pt-32 pb-20 px-6 max-w-7xl mx-auto">

        <div className="text-center max-w-3xl mx-auto mb-12 space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-900/30 border border-blue-500/30 text-blue-300 text-xs font-bold uppercase tracking-wider">
            <Zap size={12} className="fill-blue-400" />
            Upgrade your Workflow
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white tracking-tight leading-tight">
            Select Your Plan
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Unlock advanced AI reasoning (Diamond) and high-speed asset generation (Light) to build Roblox games faster.
          </p>
        </div>

        {/* Currency Banner */}
        <div className="max-w-3xl mx-auto mb-12 bg-slate-900/60 border border-slate-800 rounded-xl p-5 flex flex-col sm:flex-row gap-5 items-start">
            <div className="p-3 bg-slate-800/80 rounded-lg shrink-0">
                <Globe className="text-blue-400 w-6 h-6" />
            </div>
            <div className="space-y-2">
                <h3 className="text-white font-bold text-base flex items-center gap-2">
                    Why is the price in NGN (&#8358;)?
                </h3>
                <p className="text-sm text-slate-400 leading-relaxed">
                    We process secure payments via <strong>Paystack</strong>.
                    While valued and charged in USD, the checkout will display the equivalent in NGN due to processing regulations. 
                </p>
            </div>
        </div>

        {/* Error Message */}
        {error && (
            <div className="max-w-md mx-auto mb-8 p-4 bg-red-900/20 border border-red-500/50 text-red-200 rounded-lg text-center text-sm">
                {error}
            </div>
        )}

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {PLANS.map((plan) => (
            <PricingCard
              key={plan.id}
              plan={plan}
              loading={processingId === plan.id}
              onSelect={() => handleSubscribe(plan)}
            />
          ))}
        </div>

        <div className="mt-16 flex flex-col md:flex-row justify-center items-center gap-6 md:gap-12 text-slate-500 text-sm">
            <div className="flex items-center gap-2">
                <Lock size={16} className="text-slate-400" /> Secure SSL Payment
            </div>
         
	  <div className="flex items-center gap-2">
                <ShieldCheck size={16} className="text-slate-400" />
 <strong> Support: </strong> publicstring.care@gmail.com
            </div>

            <div className="flex items-center gap-2">
                <HelpCircle size={16} className="text-slate-400" /> 24/7 Email and Discord Support
            </div>
        </div>

        <div className="mt-8 text-center">
            <p className="text-[11px] text-slate-700 max-w-lg mx-auto leading-relaxed">
                * Refunds are available within a reasonable period. Contact support for assistance.
            </p>
        </div>

      </div>
    </div>
  );
}

// --- SUB COMPONENTS ---
function Section({ id, title, children }) {                return (                                                   <section id={id} className="scroll-mt-32 animate-in fade-in slide-in-from-bottom-4 duration-700">                 <h2 className="text-2xl font-bold text-white mb-6 flex items-center gap-3">                                       {title}                                            </h2>                                                  <div className="space-y-4 text-slate-400 leading-relaxed text-base">                                              {children}                                         </div>                                             </section>                                         );                                                 };                                                   

function PricingCard({ plan, onSelect, loading }) {
    return (
      <div className={`relative flex flex-col p-6 md:p-8 rounded-2xl border transition-all duration-300 ${
        plan.highlight
          ? 'bg-slate-900 border-blue-500/80 shadow-[0_0_50px_rgba(37,99,235,0.1)] scale-100 md:scale-105 z-10'
          : 'bg-slate-950 border-slate-800 hover:border-slate-700'
      }`}>
        {plan.highlight && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600 text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full shadow-lg border border-blue-400">
            Most Popular
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-bold text-white mb-1">{plan.name}</h3>

          <div className="flex items-end gap-2 mb-2">
            <span className="text-4xl font-extrabold text-white tracking-tight">${plan.priceUSD}</span>
            <span className="text-slate-500 text-sm font-medium mb-1.5">USD</span>
          </div>

          <div className="text-sm font-mono text-slate-300 flex items-center gap-1 opacity-90">
            <span>&#8776;</span>
            <span className="font-sans">&#8358;</span> 
            {plan.priceNGN.toLocaleString()}
          </div>
        </div>

        <div className="mb-8 space-y-4 bg-black/40 p-4 rounded-xl border border-slate-800/60">
            <div>
                <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-medium text-slate-400">Light Credits</span>
                    <span className="text-sm text-white font-bold font-mono">{plan.credits.basic.toLocaleString()}</span>
                </div>
                <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="h-full bg-slate-500 w-full opacity-60"></div>
                </div>
            </div>

            <div>
                <div className="flex justify-between items-center mb-1.5">
                    <span className="text-xs font-medium text-cyan-300 flex items-center gap-1">
                        <Zap size={10} className="fill-cyan-300" /> Diamond Credits
                    </span>
                    <span className="text-sm text-white font-bold font-mono">{plan.credits.diamond.toLocaleString()}</span>
                </div>
                <div className="w-full bg-slate-800 h-1.5 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-400 w-full animate-pulse"></div>
                </div>
            </div>
        </div>

        <div className="space-y-3 mb-8 flex-1">
          {plan.features.map((feat, i) => (
            <div key={i} className="flex items-start gap-3 text-sm text-slate-300/90">
              <div className="mt-0.5 min-w-[16px]">
                  <Check size={16} className="text-blue-500" />
              </div>
              <span className="leading-tight">{feat}</span>
            </div>
          ))}
        </div>

        <button
          onClick={onSelect}
          disabled={loading}
          className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-lg ${
            plan.highlight
            ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-blue-900/20'
            : 'bg-white text-slate-950 hover:bg-slate-200'
          } ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
        >
          {loading ? (
            <>
                <Loader2 size={16} className="animate-spin" /> Processing...
            </>
          ) : (
            `Get ${plan.name}`
          )}
        </button>
      </div>
    );
  }
